import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getCreationById, getTrackById } from '../../services/api';
import { analytics } from '../../utils/analytics';
import { getContentType } from '../../utils/contentType';
import { getTrackUrlForLocale } from '../../utils/localeUtils';
import {
  getCreatorName,
  getPlaybackMode,
  type PlaybackMode,
} from '../../utils/playlist';
import { useMusicState } from '../storeHooks/useMusicState';
import { useSessionFeatureState } from '../storeHooks/useSessionState';

interface UseAudioLoaderProps {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  currentTrackRef: React.RefObject<{
    albumId: string;
    trackIndex: number;
    trackId: string;
  } | null>;
  isSeekingRef: React.RefObject<boolean>;
  userHasInteractedRef: React.RefObject<boolean>;
  pendingPlayRef: React.RefObject<boolean>;
  isMinimized: boolean;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  setIsLoading: (loading: boolean) => void;
  setIsPlaying: (playing: boolean) => void;
  setProgress: (progress: number) => void;
  setCurrentTime: (time: number) => void;
  setDuration: (duration: number) => void;
  setPendingPlay: (pending: boolean) => void;
}

export const useAudioLoader = ({
  audioRef,
  currentTrackRef,
  isSeekingRef,
  userHasInteractedRef,
  pendingPlayRef,
  isMinimized,
  videoRef,
  setIsLoading,
  setIsPlaying,
  setProgress,
  setCurrentTime,
  setPendingPlay,
}: UseAudioLoaderProps) => {
  const location = useLocation();
  const {
    activePlaylist,
    preferredLocale,
    setActivePlaylist,
    volume,
    shuffleEnabled,
    musicPlaying,
  } = useMusicState();
  const { isConnecting, avatarReady } = useSessionFeatureState();

  const volumeRef = useRef(volume);
  const musicPlayingRef = useRef(musicPlaying);
  musicPlayingRef.current = musicPlaying;

  // Keep refs to session state so we can access latest values in callbacks
  const isConnectingRef = useRef(isConnecting);
  const avatarReadyRef = useRef(avatarReady);

  // Update refs when session state changes
  useEffect(() => {
    isConnectingRef.current = isConnecting;
    avatarReadyRef.current = avatarReady;
  }, [isConnecting, avatarReady]);
  volumeRef.current = volume;

  // Keep track of the previous locale to detect locale changes
  const previousLocaleRef = useRef(preferredLocale);

  // Prevent duplicate content_started for the same track
  const contentStartedLoggedRef = useRef<string | null>(null);

  // Check if user directly accessed a player route
  const isDirectPlayerAccess = useMemo(() => {
    const currentPath = location.pathname;
    return (
      currentPath.match(/^\/player\/[^\/]+\/?([^\/]+)?$/) ||
      currentPath.match(/^\/creations\/track\/[^\/]+\/?([^\/]+)?$/i)
    );
  }, [location.pathname]);

  // Load & play audio on track change
  useEffect(() => {
    const loadAudio = async () => {
      if (audioRef.current && activePlaylist) {
        const currentTrack =
          activePlaylist.album.tracks[activePlaylist.currentIndex];

        // Check if this is actually a new track
        const newTrackInfo = {
          albumId: activePlaylist.album.id,
          trackIndex: activePlaylist.currentIndex,
          trackId: currentTrack?.id || '',
        };

        const isNewTrack =
          !currentTrackRef.current ||
          currentTrackRef.current.albumId !== newTrackInfo.albumId ||
          currentTrackRef.current.trackId !== newTrackInfo.trackId;

        // Check if locale has changed (language switch)
        const isLocaleChanged = previousLocaleRef.current !== preferredLocale;

        // NEVER check authentication on contributor pages - they should be completely public
        const isOnContributorPage =
          window.location.pathname.startsWith('/contributor/');
        if (
          isOnContributorPage &&
          isMinimized &&
          !isNewTrack &&
          !isLocaleChanged
        ) {
          return; // Skip authentication and audio loading on contributor pages unless new track, locale change, or user clicked play
        }

        // Player routes (/player/, /playlist/, /creations/track/) are now public - no authentication required for playing music
        // Authentication is only required for actions like liking and adding to playlists

        // For browsing pages, skip audio loading if minimized and not a new track
        const isOnBrowsingPage =
          window.location.pathname.startsWith('/musicforyou') ||
          window.location.pathname.startsWith('/audiobooks') ||
          window.location.pathname.startsWith('/serenity') ||
          window.location.pathname === '/' ||
          window.location.pathname === '';

        // Skip audio loading on browsing pages unless music player is actively opened OR new track selected OR locale changed
        // NOTE: We still need to load audio if it's a NEW track or LOCALE changed, even if minimized
        if (
          isOnBrowsingPage &&
          isMinimized &&
          !isNewTrack &&
          !isLocaleChanged
        ) {
          console.log(
            '⏭️ Skipping audio load - on browsing page, minimized, and not new track or locale change',
          );
          return; // Don't load audio unless user clicked play, selected new track, or changed locale
        }

        // Update the previous locale ref
        previousLocaleRef.current = preferredLocale;

        if (isNewTrack) {
          console.log('🆕 New track detected - loading audio', {
            albumId: newTrackInfo.albumId,
            trackIndex: newTrackInfo.trackIndex,
            isOnBrowsingPage,
            isMinimized,
          });
        }

        if (currentTrack) {
          // Don't reload audio during seek operations
          if (isSeekingRef.current) {
            return;
          }

          // Determine if this is a creation (my-creations category, creation playlist category, or itemType === 'creation')
          const isCreation =
            activePlaylist?.album.category === 'my-creations' ||
            activePlaylist?.album.category === 'creation' ||
            (currentTrack as any).itemType === 'creation';

          // Check if track has URLs - only fetch if missing
          const hasUrl =
            !!currentTrack.url ||
            !!(currentTrack.urls && currentTrack.urls.length > 0);

          // Creations from My Creations only put contributors on the album until we enrich the track.
          // After getCreationById we map contributor onto the track. Video fields are optional — new
          // generations often have no previewVideo; requiring album.videoDesktopUrl kept needsFetch
          // true forever, which retriggered this effect (activePlaylist in deps) and spammed APIs + flicker.
          const hasCreationData = isCreation
            ? !!(currentTrack as any).contributors?.length
            : true;

          // Check if track has URLs, if not fetch full track details
          // For creations, also fetch if contributor data is missing
          let trackToUse = currentTrack;
          const needsFetch = !hasUrl || (isCreation && !hasCreationData);

          if (needsFetch) {
            try {
              // For creations, use getCreationById with orgId and trackId
              // For regular tracks, use getTrackById
              let fullTrackDetails;
              if (isCreation) {
                // Get orgId from multiple possible locations:
                // 1. Album orgId (for my-creations playing directly)
                // 2. Track creationOrgId (for creation playlists)
                // 3. Track orgId (alternative field)
                // 4. Track organizationId (alternative field)
                // 5. Track creation.orgId (nested field)
                const orgId =
                  (activePlaylist?.album as any)?.orgId ||
                  (currentTrack as any)?.creationOrgId ||
                  (currentTrack as any)?.orgId ||
                  (currentTrack as any)?.organizationId ||
                  (currentTrack as any)?.creation?.orgId;

                if (orgId) {
                  fullTrackDetails = await getCreationById(
                    orgId,
                    currentTrack.id,
                  );
                } else {
                  // Fallback to getTrackById if orgId not available
                  fullTrackDetails = await getTrackById(currentTrack.id);
                }
              } else {
                fullTrackDetails = await getTrackById(currentTrack.id);
              }

              if (fullTrackDetails) {
                // Normalize the track data - handle both old and new API response structures
                let normalizedTrack = { ...currentTrack, ...fullTrackDetails };

                // For creations, the response structure may be different
                // Extract data from the creation response if needed
                if (isCreation && fullTrackDetails.data) {
                  // Map contributor from creation response to contributors array format
                  // Creation API returns: contributor { id, fullName, slug, previewImage, previewVideo, previewMobileVideo, previewVideoImage, previewMobileVideoImage }
                  // Player expects: contributors[{ id, givenName, thumbnailUrl, videoDesktopUrl, videoMobileUrl, videoDesktopPreviewUrl, videoMobilePreviewUrl, slug }]
                  const creationContributor = fullTrackDetails.contributor;
                  const mappedContributors = creationContributor
                    ? [
                        {
                          id: creationContributor.id,
                          givenName: creationContributor.fullName,
                          thumbnailUrl: creationContributor.previewImage || '',
                          videoDesktopUrl:
                            creationContributor.previewVideo || '',
                          videoMobileUrl:
                            creationContributor.previewMobileVideo || '',
                          videoDesktopPreviewUrl:
                            creationContributor.previewVideoImage || '',
                          videoMobilePreviewUrl:
                            creationContributor.previewMobileVideoImage || '',
                          slug: creationContributor.slug,
                        },
                      ]
                    : normalizedTrack.contributors;

                  // Create album object with thumbnail for cover image display
                  const creationAlbum = {
                    thumbnailUrl:
                      fullTrackDetails.data.albumThumbnail ||
                      fullTrackDetails.data.previewImage ||
                      creationContributor?.previewImage ||
                      '',
                    videoDesktopUrl: creationContributor?.previewVideo || '',
                    videoMobileUrl:
                      creationContributor?.previewMobileVideo || '',
                    videoDesktopPreviewUrl:
                      creationContributor?.previewVideoImage || '',
                    videoMobilePreviewUrl:
                      creationContributor?.previewMobileVideoImage || '',
                  };

                  // Extract creator info from creation response
                  const creationCreator = fullTrackDetails.creator;

                  normalizedTrack = {
                    ...normalizedTrack,
                    url: fullTrackDetails.data.url || normalizedTrack.url,
                    name:
                      fullTrackDetails.data.title ||
                      normalizedTrack.name ||
                      fullTrackDetails.data.name,
                    duration:
                      fullTrackDetails.data.duration ||
                      normalizedTrack.duration,
                    lyrics:
                      fullTrackDetails.data.lyrics || normalizedTrack.lyrics,
                    lyricsTimestamped:
                      fullTrackDetails.data.lyricsTimestamped ||
                      normalizedTrack.lyricsTimestamped,
                    // Add contributor, album, and creator data for player UI
                    contributors: mappedContributors,
                    album: creationAlbum,
                    // Store creator info (the user who created the track)
                    creator: creationCreator
                      ? {
                          id: creationCreator.id,
                          fullName: creationCreator.fullName,
                          email: creationCreator.email,
                        }
                      : undefined,
                  };
                  console.log(
                    '✅ Normalized creation track data with contributor, album, and creator',
                  );
                }

                // If URLs are nested under audioUrl.urls, flatten them to track.urls
                if (
                  fullTrackDetails.audioUrl &&
                  fullTrackDetails.audioUrl.urls &&
                  fullTrackDetails.audioUrl.urls.length > 0
                ) {
                  normalizedTrack = {
                    ...normalizedTrack,
                    urls: fullTrackDetails.audioUrl.urls,
                  };
                  console.log(
                    '✅ Normalized track URLs from audioUrl structure',
                  );
                }

                // Check if normalized track has URLs
                if (normalizedTrack.url || normalizedTrack.urls) {
                  trackToUse = normalizedTrack;
                  console.log('✅ Full track details fetched successfully');
                  console.log(
                    'Track contributors:',
                    normalizedTrack.contributors,
                  );

                  // Update the activePlaylist with the full track details
                  // so the UI can access contributor and album data
                  if (activePlaylist) {
                    const updatedTracks = [...activePlaylist.album.tracks];
                    updatedTracks[activePlaylist.currentIndex] =
                      normalizedTrack;

                    // For creations in playlists, also update album-level fields
                    // because MiniPlayer uses album.contributors for display
                    const albumUpdates: Record<string, any> = {};
                    if (isCreation && normalizedTrack.contributors) {
                      albumUpdates.contributors = normalizedTrack.contributors;
                      // Also update album art if not set
                      if (
                        !activePlaylist.album.albumArtUrl &&
                        normalizedTrack.album?.thumbnailUrl
                      ) {
                        albumUpdates.albumArtUrl =
                          normalizedTrack.album.thumbnailUrl;
                      }
                      // Update video URLs at album level for background video
                      if (normalizedTrack.album?.videoDesktopUrl) {
                        albumUpdates.videoDesktopUrl =
                          normalizedTrack.album.videoDesktopUrl;
                      }
                      if (normalizedTrack.album?.videoMobileUrl) {
                        albumUpdates.videoMobileUrl =
                          normalizedTrack.album.videoMobileUrl;
                      }
                      if (normalizedTrack.album?.videoDesktopPreviewUrl) {
                        albumUpdates.videoDesktopPreviewUrl =
                          normalizedTrack.album.videoDesktopPreviewUrl;
                      }
                      if (normalizedTrack.album?.videoMobilePreviewUrl) {
                        albumUpdates.videoMobilePreviewUrl =
                          normalizedTrack.album.videoMobilePreviewUrl;
                      }
                    }

                    setActivePlaylist({
                      ...activePlaylist,
                      album: {
                        ...activePlaylist.album,
                        ...albumUpdates,
                        tracks: updatedTracks,
                      },
                    });
                    console.log(
                      '✅ Updated activePlaylist with full track details',
                      isCreation ? '(including album-level creation data)' : '',
                    );
                  }
                }
              }
            } catch (error) {
              console.error('❌ Failed to fetch full track details:', error);
              // Continue with original track data
            }
          }

          // Get the track URL for the preferred locale to check if it changed
          let trackUrl = getTrackUrlForLocale(trackToUse, preferredLocale);

          // Skip processing for absolute URLs (http/https)
          if (
            !trackUrl.startsWith('http://') &&
            !trackUrl.startsWith('https://')
          ) {
            // Handle relative URLs
            if (trackUrl.startsWith('/')) {
              trackUrl = trackUrl.slice(1);
            }

            if (
              !trackUrl.toLowerCase().endsWith('.mp3') &&
              !trackUrl.toLowerCase().endsWith('.wav') &&
              !trackUrl.includes(
                'api.musicgpt.com/api/public/v1/MusicAI/stream',
              )
            ) {
              trackUrl += '.mp3';
            }
          }

          // Check if this is the same track but different locale (language switch)
          // Use trackId instead of trackIndex to handle reordering
          const isSameTrack =
            currentTrackRef.current &&
            currentTrackRef.current.albumId === newTrackInfo.albumId &&
            currentTrackRef.current.trackId === newTrackInfo.trackId;

          const isUrlChanged = audioRef.current.src !== trackUrl;

          // Only reload if the track has actually changed OR if the URL changed due to locale change
          if (isSameTrack && !isUrlChanged) {
            // Same track and same URL, don't reload
            return;
          }

          // Store current playback state for language switches
          const wasPlaying = !audioRef.current.paused;
          const currentTime = audioRef.current.currentTime || 0;
          const isLanguageSwitch = isSameTrack && isUrlChanged;

          // Update the current track reference
          currentTrackRef.current = newTrackInfo;

          // Reset progress when loading new track (but not for language switches)
          if (!isLanguageSwitch) {
            setProgress(0);
            setCurrentTime(0);
            // Show loading indicator when loading a new track
            setIsLoading(true);
          }

          const audio = audioRef.current;

          // Pause and reset audio when loading a new track (not for language switches)
          if (!isLanguageSwitch) {
            try {
              audio.pause();
              audio.currentTime = 0;
              // Reset isPlaying state when loading new track
              setIsPlaying(false);
            } catch (error) {
              console.warn('Failed to pause/reset audio:', error);
            }
          }

          // Set audio source directly - HTML audio elements handle CORS differently than fetch()
          // and work fine with Azure Blob Storage without triggering preflight requests
          audio.src = trackUrl;

          // Flag to ensure handleCanPlay only runs once
          let handleCanPlayCalled = false;

          // Wait for enough data to be loaded before playing
          const handleCanPlay = () => {
            const currentUserInteracted = userHasInteractedRef.current;
            const currentPendingPlay = pendingPlayRef.current;

            console.log('🎵 handleCanPlay called', {
              currentUserInteracted,
              currentPendingPlay,
              isDirectPlayerAccess,
              audioPaused: audio.paused,
              readyState: audio.readyState,
              duration: audio.duration,
              handleCanPlayCalled,
            });

            // Validate audio state before proceeding
            // Don't set handleCanPlayCalled if audio isn't ready - allow retry
            if (!audio || !isFinite(audio.duration) || audio.duration <= 0) {
              console.warn(
                'Audio not ready: Invalid duration - will retry on next event',
                {
                  duration: audio?.duration,
                  readyState: audio?.readyState,
                  networkState: audio?.networkState,
                },
              );
              // Don't set handleCanPlayCalled here - allow future events to try again
              return;
            }

            // Prevent handleCanPlay from running multiple times AFTER audio is ready
            if (handleCanPlayCalled) {
              console.log(
                '⏭️ handleCanPlay already completed successfully, skipping',
              );
              return;
            }
            handleCanPlayCalled = true;

            setIsLoading(false);

            // Validate currentTime is finite
            if (!isFinite(audio.currentTime)) {
              console.warn('Audio currentTime is not finite, resetting to 0');
              try {
                audio.currentTime = 0;
              } catch (error) {
                console.error('Failed to reset currentTime:', error);
                setIsLoading(false);
                return;
              }
            }

            setIsLoading(false);

            // Sync volume with Redux state after audio loads
            // This ensures muted state persists when opening new tracks
            audio.volume = volumeRef.current;

            // For language switches, restore the previous playback position and state
            if (isLanguageSwitch && currentTime > 0) {
              try {
                audio.currentTime = currentTime;
              } catch (error) {
                console.warn(
                  'Failed to restore currentTime for language switch:',
                  error,
                );
              }
            }

            // Auto-play if user has interacted OR if there's a pending play request OR if it was playing during language switch OR if user directly accessed player route
            // BUT: Don't auto-play if audio is already paused by user
            const shouldAutoPlay =
              (currentUserInteracted ||
                currentPendingPlay ||
                (isLanguageSwitch && wasPlaying) ||
                isDirectPlayerAccess) &&
              audio.paused; // Only auto-play if audio is currently paused (not already playing)

            if (shouldAutoPlay) {
              setPendingPlay(false); // Clear pending play

              audio
                .play()
                .then(() => {
                  setIsPlaying(true);
                  // Log content_started once per track
                  if (activePlaylist) {
                    const album = activePlaylist.album;
                    const track = album.tracks[activePlaylist.currentIndex];
                    const trackId = track?.id || '';
                    if (
                      trackId &&
                      contentStartedLoggedRef.current !== trackId
                    ) {
                      contentStartedLoggedRef.current = trackId;

                      // Determine playback mode
                      const playbackMode: PlaybackMode = getPlaybackMode({
                        shuffleEnabled,
                        album,
                      });

                      // Determine creator name via shared util
                      const creatorName = getCreatorName({
                        album,
                        track,
                      });

                      analytics.logEvent('content_started', {
                        content_type: getContentType(album.category),
                        content_id: trackId,
                        content_name: track?.name || 'Unknown',
                        creator_name: creatorName,
                        source_page: `${location.pathname}${
                          location.search || ''
                        }`,
                        playback_mode: playbackMode,
                      });
                    }
                  }
                  if (videoRef.current && !isMinimized) {
                    videoRef.current.play().catch(() => {
                      console.warn(
                        'Video play failed, continuing with audio only',
                      );
                    });
                  }
                })
                .catch((error) => {
                  console.error(
                    '❌ Audio play failed:',
                    error,
                    'audio.paused:',
                    audio.paused,
                  );
                  setIsPlaying(false);
                  // If auto-play fails and user hasn't manually interacted, show popup
                  if (!currentUserInteracted && !isDirectPlayerAccess) {
                    setPendingPlay(true);
                  }
                });
            } else {
              // On mobile direct URL access, wait for user interaction
              console.log(
                '⏸️ Not auto-playing - waiting for user interaction',
                {
                  currentUserInteracted,
                  currentPendingPlay,
                  isLanguageSwitch,
                  wasPlaying,
                  isDirectPlayerAccess,
                  audioPaused: audio.paused,
                },
              );
              setIsPlaying(false);
            }
          };

          // Alternative handler for Safari - try multiple events
          const handleLoadedData = () => {
            // If canplay hasn't fired yet, use this as backup
            if (audio.readyState >= 2) {
              // HAVE_CURRENT_DATA or higher
              handleCanPlay();
            }
          };

          const handleCanPlayThrough = () => {
            // This is even better than canplay - entire audio can play without stopping
            handleCanPlay();
          };

          // Fallback: Set a timeout to stop loading state even if events don't fire
          const fallbackTimeout = setTimeout(() => {
            if (setIsLoading) {
              setIsLoading(false);
            }
          }, 5000); // 5 second timeout

          const handleError = (event: Event) => {
            const errorTarget = event.target as HTMLAudioElement;
            const errorCode = errorTarget?.error?.code;
            const errorMessage = errorTarget?.error?.message;

            console.error('Audio loading error:', {
              error: event,
              errorCode,
              errorMessage,
              networkState: audio.networkState,
              readyState: audio.readyState,
              src: audio.src,
              originalUrl: getTrackUrlForLocale(currentTrack, preferredLocale),
            });

            // Try alternative URL formats if the current one fails
            const currentSrc = audio.src;
            let retryUrl = null;

            if (currentSrc.includes('.wav.mp3')) {
              // Try without .wav extension
              retryUrl = currentSrc.replace('.wav.mp3', '.mp3');
              console.log('Retrying with cleaned URL:', retryUrl);
            } else if (currentSrc.endsWith('.mp3')) {
              // Try with .wav extension
              retryUrl = currentSrc.replace('.mp3', '.wav');
              console.log('Retrying with .wav extension:', retryUrl);
            }

            if (retryUrl && retryUrl !== currentSrc) {
              console.log('Attempting retry with alternative URL:', retryUrl);
              audio.src = retryUrl;
              // Don't set isPlaying to false yet, give retry a chance
              return;
            }

            // All retry attempts failed
            console.error(
              'All audio loading attempts failed for track:',
              currentTrack.name,
            );
            setIsPlaying(false);
            setIsLoading(false);
          };

          const handleLoadStart = () => {
            setIsLoading(true);

            // Set a timeout to detect loading issues
            setTimeout(() => {
              if (audio.readyState === 0 && audio.networkState === 3) {
                console.error(
                  'Audio loading timeout - file may not exist or be accessible',
                );
                setIsLoading(false);
              }
            }, 10000); // 10 second timeout
          };

          const handleProgress = () => {
            // Audio buffering progress - keeping minimal for performance
          };

          // Remove previous listeners
          audio.removeEventListener('canplay', handleCanPlay);
          audio.removeEventListener('canplaythrough', handleCanPlayThrough);
          audio.removeEventListener('loadeddata', handleLoadedData);
          audio.removeEventListener('loadedmetadata', handleCanPlay);
          audio.removeEventListener('error', handleError);
          audio.removeEventListener('loadstart', handleLoadStart);
          audio.removeEventListener('progress', handleProgress);

          // Add comprehensive event listeners - try multiple events for Safari compatibility
          audio.addEventListener('canplay', handleCanPlay, { once: true });
          audio.addEventListener('canplaythrough', handleCanPlayThrough, {
            once: true,
          });
          audio.addEventListener('loadeddata', handleLoadedData, {
            once: true,
          });
          audio.addEventListener('loadedmetadata', handleCanPlay, {
            once: true,
          });
          audio.addEventListener('error', handleError, { once: true });
          audio.addEventListener('loadstart', handleLoadStart, { once: true });
          audio.addEventListener('progress', handleProgress);

          // Force load the audio
          audio.load();

          // If audio is already ready (readyState >= 2), call handleCanPlay immediately
          // This handles the case where the audio was cached or already loaded
          if (audio.readyState >= 2) {
            console.log(
              '⚡ Audio already ready, calling handleCanPlay immediately',
              {
                readyState: audio.readyState,
              },
            );
            // Use setTimeout to ensure it's called after event listeners are attached
            setTimeout(() => {
              handleCanPlay();
            }, 0);
          }

          // Fallback timeout - if no events fire within 3 seconds, try to play anyway
          const fallbackPlayTimeout = setTimeout(() => {
            const isSessionActive =
              isConnectingRef.current || avatarReadyRef.current;
            const isExplicitUserPlay = pendingPlayRef.current;

            console.log(
              '⏱️ Fallback timeout triggered - attempting to play without canplay event',
              {
                readyState: audio.readyState,
                duration: audio.duration,
                handleCanPlayCalled,
                userHasInteracted: userHasInteractedRef.current,
                pendingPlay: pendingPlayRef.current,
                isSessionActive,
                isConnecting: isConnectingRef.current,
                avatarReady: avatarReadyRef.current,
              },
            );

            // Don't auto-play if session is active, unless user explicitly requested play
            if (isSessionActive && !isExplicitUserPlay) {
              console.log('⏸️ Skipping fallback autoplay - session is active');
              return;
            }

            // Only force play when Redux says we should be playing (or direct player load).
            // Otherwise we'd restart playback when user had paused during the 3s window.
            if (
              handleCanPlayCalled &&
              audio.paused &&
              audio.readyState >= 2 &&
              (musicPlayingRef.current || isDirectPlayerAccess)
            ) {
              console.log('🔄 Forcing autoplay after fallback timeout');
              audio
                .play()
                .then(() => {
                  setIsPlaying(true);
                })
                .catch((error) => {
                  console.error('Fallback autoplay failed:', error);
                });
            } else if (!handleCanPlayCalled) {
              // handleCanPlay never ran, try it now
              handleCanPlay();
            }
          }, 3000);

          // Cleanup function
          return () => {
            clearTimeout(fallbackTimeout);
            clearTimeout(fallbackPlayTimeout);

            audio.removeEventListener('canplay', handleCanPlay);
            audio.removeEventListener('canplaythrough', handleCanPlayThrough);
            audio.removeEventListener('loadeddata', handleLoadedData);
            audio.removeEventListener('error', handleError);
            audio.removeEventListener('loadstart', handleLoadStart);
            audio.removeEventListener('progress', handleProgress);
          };
        }
      }
    };

    loadAudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activePlaylist,
    isMinimized,
    preferredLocale,
    isDirectPlayerAccess,
    location.pathname,
    location.search,
  ]);
};
